
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CustomerListItem, User } from '../../types';
import { LuBuilding } from "react-icons/lu";
import { GoSignOut } from "react-icons/go";
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { HomeIcon } from '@heroicons/react/24/outline';
import { HomeIcon as HomeSolid } from '@heroicons/react/24/solid';
import { userApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  recentCustomers: CustomerListItem[];
  onNewCustomerClick: () => void;
  isLoadingRecentCustomers?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ recentCustomers, onNewCustomerClick, isLoadingRecentCustomers = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {signOut } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | undefined>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await userApi.getCurrentUser();
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-profile-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        } finally {
  
        }
    };

  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: HomeIcon,
      iconActive: HomeSolid,
    },
    // Hidden until functionality is implemented
    // {
    //   name: 'Integrations',
    //   path: '/integrations',
    //   icon: RocketLaunchIcon,
    //   iconActive: RocketSolid,
    // },
    // {
    //   name: 'Settings',
    //   path: '/settings',
    //   icon: Cog6ToothIcon,
    //   iconActive: CogSolid,
    // },
  ];

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <aside className="w-64 h-screen bg-white border-r px-4 py-6 flex flex-col justify-between">
      {/* Top Section */}
      <div>
        <div className="mb-6">
          <h1 className="text-xl cursor-pointer font-bold italic text-[#0F2A4F]" onClick={() => navigate('/')}>TechProfile <span className="not-italic">2.0</span></h1>
        </div>

        <button
          onClick={onNewCustomerClick}
          className="w-full bg-[#0F2A4F] text-white font-medium py-2 px-4 rounded-md text-sm hover:bg-[#1d395e]"
        >
          + New Customer
        </button>

        {/* Navigation */}
        <nav className="mt-8 space-y-1">
          {navItems.map(({ name, path, icon: Icon, iconActive: IconActive }) => {
            const active = isActive(path);
            return (
              <button
                key={name}
                onClick={() => navigate(path)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md ${
                  active
                    ? 'bg-gray-100 text-blue-600 font-medium'
                    : 'text-[#0F2A4F] hover:bg-gray-50'
                }`}
              >
                {active ? (
                  <IconActive className="w-4 h-4 text-blue-600" />
                ) : (
                  <Icon className="w-4 h-4 text-[#0F2A4F]" />
                )}
                {name}
              </button>
            );
          })}
        </nav>

        {/* Recent Customers */}
        <div className="mt-10">
          <p className="text-xs text-zinc-500 font-semibold mb-2">RECENT CUSTOMERS</p>
          {isLoadingRecentCustomers ? (
            <div className="space-y-2">
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex gap-2 items-center px-2 py-1">
                  <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse flex-1"></div>
                </div>
              ))}
            </div>
          ) : recentCustomers?.length > 0 ? (
            recentCustomers.map((cust) => (
              <button
                key={cust.customer_id}
                onClick={() => navigate(`/customer-details/${cust.customer_id}`)}
                className="text-sm text-[#0F2A4F] px-2 py-1 rounded hover:bg-gray-100 cursor-pointer truncate w-full"
                title={cust.name}
              >
                <div className="flex gap-2 items-center">
                    <LuBuilding /> {cust.name}
                </div>
              </button>
            ))
          ) : (
            <li className="text-sm text-gray-500">No recent customers</li>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-sm text-gray-500">
        {/* User Profile Section */}
        <div className="relative user-profile-dropdown">
          <div 
            className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-50 transition-colors rounded"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="flex items-center gap-3">
              {currentUser?.avatar ? (
                <img 
                  src={currentUser.avatar} 
                  alt={currentUser.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#0F2A4F] text-white flex items-center justify-center text-sm font-medium">
                  {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <span className="font-medium text-[#0F2A4F] truncate">
                {currentUser?.name || 'Loading...'}
              </span>
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </div>
          
          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-0 left-full ml-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
              <button
                onClick={() => handleSignOut()}
                className="w-full text-left px-3 py-2 text-sm text-[#0F2A4F] hover:bg-gray-50 rounded-lg flex items-center gap-2"
              >
                <GoSignOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;